import { memo } from 'react';
import {
  Field,
  FileField,
  ImageField,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import { ComponentProps, DynamicHeading, resolveColorProperty } from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import VideoBackground from 'composables/Banners/VideoBanner/VideoBackground';
import {
  ProductItem,
  VideoBannerProducts,
} from 'composables/Banners/VideoBanner/VideoBannerProducts';
import NavButton from 'composables/shared/NavButton/NavButton';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

export type VideoBannerFields = {
  videoLg: FileField;
  videoMd: FileField;
  videoXs: FileField;
  image: ImageField;
  title: Field<string>;
  titleSmall: Field<string>;
  eyebrowTitle: Field<string>;
  titleFontSizeStyle: Field<string>;
  titleSmallFontSizeStyle: Field<string>;
  primaryButtonText: Field<string>;
  primaryButtonAriaLabel?: Field<string>;
  primaryButtonLink: LinkField;
  secondaryButtonText: Field<string>;
  secondaryButtonAriaLabel?: Field<string>;
  secondaryButtonLink: LinkField;
  backgroundContrast: Field<number>;
  primaryButtonIcon?: Field<string>;
  secondaryButtonIcon?: Field<string>;
  isMainHeading: Field<boolean>;
  withProductsSection: Field<boolean>;
  productsList: ProductItem[];
  mainHeadingColor: Field<string>;
  descriptionColor: Field<string>;
  eyebrowColor: Field<string>;
  firstCtaButtonTrackingName?: Field<string>;
  secondCtaButtonTrackingName?: Field<string>;
  trackingSpotName?: Field<string>;
} & VideoBannerProducts;

export type VideoBannerProps = ComponentProps & {
  fields: VideoBannerFields;
};

const VideoBanner = ({ fields }: VideoBannerProps) => {
  const {
    videoLg,
    videoMd,
    videoXs,
    image,
    title,
    titleSmall,
    eyebrowTitle,
    titleFontSizeStyle,
    titleSmallFontSizeStyle,
    backgroundContrast,
    primaryButtonLink,
    primaryButtonText,
    primaryButtonAriaLabel,
    secondaryButtonLink,
    secondaryButtonText,
    secondaryButtonAriaLabel,
    withProductsSection,
    productsList,
    explanatoryText,
    explanatoryBackgroundColor,
    isMainHeading,
    primaryButtonIcon,
    secondaryButtonIcon,
    mainHeadingColor,
    descriptionColor,
    eyebrowColor,
    firstCtaButtonTrackingName,
    secondCtaButtonTrackingName,
    trackingSpotName,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);

  const shouldRenderButtons =
    (!!primaryButtonLink?.value?.href && !!primaryButtonText?.value) ||
    (!!secondaryButtonLink?.value?.href && !!secondaryButtonText?.value);

  const fallbackTextColor = '#FFFFFF';
  const mainHeadingColorStyle =
    resolveColorProperty(mainHeadingColor?.value as string) || fallbackTextColor;
  const descriptionColorStyle =
    resolveColorProperty(descriptionColor?.value as string) || fallbackTextColor;
  const eyebrowColorStyle =
    resolveColorProperty(eyebrowColor?.value as string) || fallbackTextColor;

  return (
    <VideoBackground
      videoLg={videoLg}
      videoMd={videoMd}
      videoXs={videoXs}
      image={image}
      videoContainerClass="min-h-screen flex"
      backgroundContrast={backgroundContrast?.value}
      reducedWidth={false}
      hideControlsOnMobile={true}
    >
      <div
        ref={spotRef}
        data-wt-spot={trackingSpotName?.value}
        className={classNames(
          'container flex min-h-full max-w-6xl flex-col items-center justify-center overflow-hidden',
          withProductsSection?.value && 'pb-80 pt-24'
        )}
      >
        {!!eyebrowTitle?.value && (
          <Eyebrow
            style={{ color: eyebrowColorStyle }}
            text={eyebrowTitle.value as string}
            align="center"
          />
        )}

        {!!title?.value && (
          <DynamicHeading
            className="mt-5 text-center text-[2.25rem] font-bold leading-[110%] md:text-[5rem]"
            style={{ fontSize: titleFontSizeStyle?.value, color: mainHeadingColorStyle }}
            isMainHeading={isMainHeading?.value}
          >
            <span dangerouslySetInnerHTML={{ __html: sanitize(title.value as string) }} />
          </DynamicHeading>
        )}

        {!!titleSmall?.value && (
          <span
            className="mt-5 block text-center text-2xl font-normal leading-[110%] md:text-[2.25rem]"
            style={{ fontSize: titleSmallFontSizeStyle?.value, color: descriptionColorStyle }}
          >
            <span dangerouslySetInnerHTML={{ __html: sanitize(titleSmall.value as string) }} />
          </span>
        )}

        {shouldRenderButtons && (
          <div className="dark mt-[30px] flex flex-wrap justify-center gap-6">
            {!!primaryButtonText?.value && (
              <NavButton
                variant="primary"
                size="m"
                link={primaryButtonLink}
                title={primaryButtonText?.value}
                ariaLabel={primaryButtonAriaLabel?.value}
                {...(firstCtaButtonTrackingName?.value && {
                  'data-wt-name': firstCtaButtonTrackingName.value,
                })}
                icon={primaryButtonIcon?.value}
              />
            )}
            {!!secondaryButtonText?.value && (
              <NavButton
                variant="secondary"
                size="m"
                link={secondaryButtonLink}
                title={secondaryButtonText?.value}
                ariaLabel={secondaryButtonAriaLabel?.value}
                {...(secondCtaButtonTrackingName?.value && {
                  'data-wt-name': secondCtaButtonTrackingName.value,
                })}
                icon={secondaryButtonIcon?.value}
              />
            )}
          </div>
        )}
      </div>
      <VideoBannerProducts
        withProductsSection={withProductsSection}
        explanatoryBackgroundColor={explanatoryBackgroundColor}
        explanatoryText={explanatoryText}
        productsList={productsList}
      />
    </VideoBackground>
  );
};

export const VideoBannerMemo = memo(VideoBanner, (prevProps, nextProps) => {
  const prevFields = prevProps.fields;
  const nextFields = nextProps.fields;

  return (
    prevFields.title?.value === nextFields.title?.value &&
    prevFields.titleSmall?.value === nextFields.titleSmall?.value &&
    prevFields.eyebrowTitle?.value === nextFields.eyebrowTitle?.value &&
    prevFields.primaryButtonText?.value === nextFields.primaryButtonText?.value &&
    prevFields.secondaryButtonText?.value === nextFields.secondaryButtonText?.value &&
    prevFields.withProductsSection?.value === nextFields.withProductsSection?.value &&
    prevFields.backgroundContrast?.value === nextFields.backgroundContrast?.value &&
    prevFields.videoLg?.value?.src === nextFields.videoLg?.value?.src &&
    prevFields.videoMd?.value?.src === nextFields.videoMd?.value?.src &&
    prevFields.videoXs?.value?.src === nextFields.videoXs?.value?.src &&
    prevFields.image?.value?.src === nextFields.image?.value?.src
  );
});

export default withDatasourceCheck()<VideoBannerProps>(VideoBannerMemo);
